
import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, Film, Zap, CheckCircle, X, Target, Clock, Palette, Share2, TrendingUp, Users, DollarSign, Code, Video, PlayCircle, Globe } from "lucide-react";

const Landing = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-sm bg-black/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Film className="h-8 w-8 text-purple-400 mr-3" />
            <h1 className="text-2xl font-bold text-white">wedemo</h1>
          </div>
          <Button 
            className="bg-purple-600 hover:bg-purple-700 text-white cursor-pointer"
            data-tally-open="n0zy7P"
            data-tally-emoji-text="👋"
            data-tally-emoji-animation="wave"
          >
            Get Early Access
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 text-sm mb-8">
            <Sparkles className="h-4 w-4 mr-2" />
            AI-Powered Demo Creation
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
            Turn Screen Recordings Into
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400"> Shareable Product Demos</span>, Instantly
          </h1>
          
          <h2 className="text-2xl md:text-3xl font-semibold text-gray-200 mb-8">
            AI-Powered Demo Creation That Takes 5 Minutes, Not Hours
          </h2>
          
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            <strong>Wedemo</strong> transforms your raw screen recordings into polished, professional product demos automatically. No video editing skills required — just record your workflow and let our AI handle the rest.
          </p>
          
          <p className="text-lg text-purple-300 mb-12">
            <strong>Perfect for:</strong> SaaS teams, product managers, and founders who need professional demos without the hassle.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg" 
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg cursor-pointer"
              data-tally-open="n0zy7P"
              data-tally-emoji-text="👋"
              data-tally-emoji-animation="wave"
            >
              Get Early Access — Join Waitlist
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>

          {/* Demo Video */}
          <div className="max-w-4xl mx-auto">
            <video
              autoPlay
              muted
              loop
              playsInline
              controls
              className="w-full max-w-3xl mx-auto rounded-lg shadow-2xl border border-white/10"
            >
              <source src="/demo-waitlist.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 px-6 bg-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Here's How It Works (3 Simple Steps)
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 mx-auto rounded-full bg-blue-500/20 flex items-center justify-center">
                <Video className="h-8 w-8 text-blue-400" />
              </div>
              <div className="text-2xl font-bold text-blue-400 mb-2">1. Record Your Screen</div>
              <p className="text-gray-300">
                Capture your product workflow using our Chrome extension. Don't worry about mistakes — we'll fix them.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-purple-400" />
              </div>
              <div className="text-2xl font-bold text-purple-400 mb-2">2. AI Does the Magic</div>
              <p className="text-gray-300">
                Our smart editor automatically removes pauses, highlights key actions, adds smooth transitions, and applies your branding. Zero editing required.
              </p>
            </div>
            
            <div className="text-center p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
                <Share2 className="h-8 w-8 text-green-400" />
              </div>
              <div className="text-2xl font-bold text-green-400 mb-2">3. Share & Convert</div>
              <p className="text-gray-300">
                Get a shareable link or embed code in minutes. Your demo is ready for onboarding, sales, or support — wherever you need it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 text-center">
            The Problem Every SaaS Team Faces
          </h2>
          
          
          <div className="grid md:grid-cols-1 gap-6">
            {[
              "Screen recordings look amateur and hurt your brand",
              "Video editing takes forever — hours you don't have",
              "Users get confused during onboarding and abandon", 
              "Support tickets pile up with 'how do I...?' questions",
              "Feature launches fail without proper demos"
            ].map((problem, index) => (
              <div key={index} className="flex items-start p-4 rounded-lg bg-red-500/10 border border-red-500/20">
                <X className="h-6 w-6 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                <span className="text-gray-200">{problem}</span>
              </div>
            ))}
          </div>
          <p className="text-xl text-gray-300 mt-12 text-center">
            <strong>Sound familiar?</strong> You're not alone. Most teams either skip demos entirely or spend way too much time creating them.
          </p>
        </div>
      </section>

      {/* What Makes Wedemo Different Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              What Makes Wedemo Different
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Target className="h-8 w-8 text-purple-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Record Once, Perfect Automatically</h3>
              <p className="text-gray-300">
                Our AI editor removes mistakes, highlights important clicks, and adds professional polish. No manual editing needed.
              </p>
            </div>
            
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Zap className="h-8 w-8 text-blue-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">5-Minute Demos That Drive Results</h3>
              <p className="text-gray-300">
                From recording to sharing: under 5 minutes total. Create demos as fast as you ship features.
              </p>
            </div>
            
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
                <Palette className="h-8 w-8 text-green-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Always On-Brand & Professional</h3>
              <p className="text-gray-300">
                Every demo gets consistent branding, crisp quality, and smooth animations that reflect your product's quality.
              </p>
            </div>
            
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10">
              <div className="w-16 h-16 mb-6 rounded-full bg-orange-500/20 flex items-center justify-center">
                <Share2 className="h-8 w-8 text-orange-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Share Anywhere, Instantly</h3>
              <p className="text-gray-300">
                Drop demos into help docs, email campaigns, sales presentations, or embed directly on your website.
              </p>
            </div>
            
            <div className="p-8 rounded-2xl bg-white/5 border border-white/10 md:col-span-2">
              <div className="w-16 h-16 mb-6 rounded-full bg-pink-500/20 flex items-center justify-center">
                <TrendingUp className="h-8 w-8 text-pink-400" />
              </div>
              <h3 className="text-2xl font-semibold text-white mb-4">Onboarding That Actually Works</h3>
              <p className="text-gray-300">
                Transform confused new users into engaged customers with step-by-step visual guides.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 px-6 bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-16 text-center">
            Wedemo vs. Traditional Tools
          </h2>
          
          <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              <div className="p-8 border-r border-white/10">
                <h3 className="text-xl font-semibold text-gray-300 mb-6 text-center">Traditional Screen Recorders</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Generic, one-size-fits-all</span>
                  </div>
                  <div className="flex items-start">
                    <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Hours of manual editing</span>
                  </div>
                  <div className="flex items-start">
                    <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Amateur-looking results</span>
                  </div>
                  <div className="flex items-start">
                    <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Complicated workflows</span>
                  </div>
                  <div className="flex items-start">
                    <X className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Static video files</span>
                  </div>
                </div>
              </div>
              
              <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10">
                <h3 className="text-xl font-semibold text-white mb-6 text-center">Wedemo</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200"><strong>Built specifically for SaaS products</strong></span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200"><strong>AI handles everything automatically</strong></span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200"><strong>Pixel-perfect, branded demos</strong></span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200"><strong>Record → AI processes → Share</strong></span>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-200"><strong>Interactive, embeddable demos</strong></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <p className="text-gray-400 text-center mt-8 italic">
            While Loom captures meetings and other tools make basic tutorials, Wedemo is purpose-built for SaaS teams who need professional product demos that actually convert.
          </p>
        </div>
      </section>

      {/* Target Users Section */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-16 text-center">
            Who Should Use Wedemo?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20">
              <div className="flex items-center mb-4">
                <Code className="h-8 w-8 text-purple-400 mr-3" />
                <h3 className="text-2xl font-semibold text-white">Product Teams</h3>
              </div>
              <p className="text-purple-200 italic mb-4">"We demo new features the same day we ship them"</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Launch features with instant video walkthroughs</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Reduce support burden with self-service demos</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Boost feature adoption rates</li>
              </ul>
            </div>
            
            <div className="p-8 rounded-2xl bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20">
              <div className="flex items-center mb-4">
                <Users className="h-8 w-8 text-blue-400 mr-3" />
                <h3 className="text-2xl font-semibold text-white">Customer Success</h3>
              </div>
              <p className="text-blue-200 italic mb-4">"Our customers finally understand our product"</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Create personalized onboarding flows</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Answer support questions with visual responses</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Increase user engagement and retention</li>
              </ul>
            </div>
            
            <div className="p-8 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20">
              <div className="flex items-center mb-4">
                <DollarSign className="h-8 w-8 text-green-400 mr-3" />
                <h3 className="text-2xl font-semibold text-white">Sales & Marketing</h3>
              </div>
              <p className="text-green-200 italic mb-4">"These demos close deals faster than any pitch"</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Show prospects exactly how you solve their problems</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Create compelling feature announcements</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Build demo libraries for every use case</li>
              </ul>
            </div>
            
            <div className="p-8 rounded-2xl bg-gradient-to-br from-orange-500/10 to-red-500/10 border border-orange-500/20">
              <div className="flex items-center mb-4">
                <Sparkles className="h-8 w-8 text-orange-400 mr-3" />
                <h3 className="text-2xl font-semibold text-white">Founders & Developers</h3>
              </div>
              <p className="text-orange-200 italic mb-4">"We look like a much bigger company than we are"</p>
              <ul className="space-y-2 text-gray-300">
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Professional demos without hiring a video team</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Document product updates effortlessly</li>
                <li className="flex items-start"><CheckCircle className="h-5 w-5 text-green-400 mr-2 mt-0.5 flex-shrink-0" />Showcase your work with confidence</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
            Ready to Stop Wasting Time on Demos?
          </h2>
          
          <p className="text-xl text-gray-300 mb-12">
            <strong>Join forward-thinking teams</strong> who've upgraded from amateur screen recordings to professional, AI-powered demos.
          </p>
          
          <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-semibold text-white mb-6 flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-purple-400 mr-2" />
              Early Access Benefits
            </h3>
            
            <div className="grid md:grid-cols-2 gap-4 text-left">
              {[
                "🎁 50% off your first year",
                "Priority onboarding with our team",
                "Beta feature access before anyone else",
                "Direct line to the founder for feedback"
              ].map((benefit, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-200">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-6 mb-12">
            <Button 
              size="lg" 
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 text-lg cursor-pointer"
              data-tally-open="n0zy7P"
              data-tally-emoji-text="👋"
              data-tally-emoji-animation="wave"
            >
              Get Early Access — Join Waitlist
              <ArrowRight className="ml-2 h-6 w-6" />
            </Button>
            
            <div className="space-y-2">
              <p className="text-gray-400 text-sm">
                <strong>Why join the waitlist?</strong>
              </p>
              <ul className="text-gray-400 text-sm space-y-1">
                <li>• See exactly what features we're building based on user feedback</li>
                <li>• Get first access when we launch</li>
                <li>• Lock in special pricing before we go public</li>
              </ul>
              <p className="text-gray-400 text-sm">
                <em>No spam, no commitment. Just first access to the demo tool that'll transform how your team communicates.</em>
              </p>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8">
            <p className="text-purple-300 mb-2">
              <strong>Questions?</strong> Email me at <a href="mailto:steven@wedemo.app" className="text-purple-400 hover:text-purple-300 underline">steven@wedemo.app</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6 mt-20">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Film className="h-6 w-6 text-purple-400 mr-2" />
            <span className="text-white font-semibold">wedemo</span>
          </div>
          <p className="text-gray-400 mb-2">
            Where screen recordings become professional demos.
          </p>
          <p className="text-gray-500 text-sm">
            © 2025 wedemo. Inventing the future of demo creation with AI.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
